/* eslint-disable no-unused-vars */
import { useEffect, useState, useRef } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import config from "../../config";
import makeRequest from "../../makeRequest";

/* ─── Injected styles ──────────────────────────────────────────── */
const STYLE_ID = "testimonial-page-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes tFadeUp {
      from { opacity: 0; transform: translateY(32px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes tShimmer {
      0%   { background-position: -600px 0; }
      100% { background-position: 600px 0; }
    }
    @keyframes tFloat {
      0%, 100% { transform: translateY(0); }
      50%       { transform: translateY(-8px); }
    }

    .tcard {
      background: #fff;
      border-radius: 18px;
      padding: 36px 32px 28px;
      position: relative;
      border: 1px solid #e8eef5;
      box-shadow: 0 8px 28px rgba(10, 44, 88, 0.07);
      display: flex;
      flex-direction: column;
      transition: transform 0.28s ease, box-shadow 0.28s ease;
    }
    .tcard:hover {
      transform: translateY(-7px);
      box-shadow: 0 20px 48px rgba(10, 44, 88, 0.13);
    }

    .tcard-quote {
      position: absolute;
      top: 24px;
      right: 28px;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: linear-gradient(135deg, #1b74bb, #0d3b6e);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 20px;
      line-height: 1;
      opacity: 0.92;
    }

    .tcard-stars {
      display: flex;
      gap: 3px;
      margin-bottom: 18px;
    }
    .tcard-stars i {
      color: #f59e0b;
      font-size: 15px;
    }

    .tcard-message {
      font-size: 15px;
      line-height: 1.82;
      color: #475569;
      flex: 1;
      margin: 0 0 26px;
    }

    .tcard-divider {
      height: 1px;
      background: linear-gradient(to right, #e2eaf4, transparent);
      margin-bottom: 22px;
    }

    .tcard-author {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .tcard-avatar {
      width: 52px;
      height: 52px;
      border-radius: 50%;
      flex-shrink: 0;
      overflow: hidden;
      border: 2.5px solid #1b74bb22;
    }
    .tcard-avatar img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .tcard-avatar-initials {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #1b74bb, #0d3b6e);
      color: #fff;
      font-size: 18px;
      font-weight: 700;
      letter-spacing: 0.5px;
    }
    .tcard-name {
      font-size: 16px;
      font-weight: 700;
      color: #0f1f44;
      margin: 0 0 3px;
    }
    .tcard-role {
      font-size: 13px;
      color: #1b74bb;
      font-weight: 500;
      margin: 0;
    }

    /* Verified badge */
    .tcard-verified {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      background: #edf7f0;
      border: 1px solid #bbdfc8;
      border-radius: 50px;
      font-size: 11px;
      font-weight: 600;
      color: #166534;
      padding: 3px 10px;
      margin-left: auto;
    }
    .tcard-verified i { font-size: 11px; }

    /* Grid */
    .testimonial-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 28px;
    }
    @media (max-width: 1024px) {
      .testimonial-grid { grid-template-columns: repeat(2, 1fr); gap: 22px; }
    }
    @media (max-width: 640px) {
      .testimonial-grid { grid-template-columns: 1fr; gap: 18px; }
      .tcard { padding: 28px 22px 22px; }
    }

    /* Skeleton shimmer */
    .t-shimmer {
      background: linear-gradient(90deg, #e8eef5 0%, #f6f9fc 50%, #e8eef5 100%);
      background-size: 600px 100%;
      animation: tShimmer 1.5s infinite linear;
      border-radius: 8px;
    }

    /* Stats bar pill */
    .t-stat-pill {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      padding: 18px 32px;
      background: rgba(255,255,255,0.09);
      border: 1px solid rgba(255,255,255,0.15);
      border-radius: 16px;
      backdrop-filter: blur(6px);
    }
    .t-stat-pill strong {
      font-size: 30px;
      font-weight: 800;
      color: #fff;
      line-height: 1;
    }
    .t-stat-pill span {
      font-size: 12px;
      color: rgba(255,255,255,0.72);
      margin-top: 4px;
      font-weight: 500;
      letter-spacing: 0.5px;
    }
  `;
  document.head.appendChild(s);
}

/* ─── Avatar with blob image fetch ────────────────────────────── */
function Avatar({ id, name, hasImage }) {
  const [src, setSrc] = useState(null);
  const baseUrl = config.api.baseUrl;

  useEffect(() => {
    if (!hasImage) return;
    let url = null;
    fetch(`${baseUrl}/website/testimonial/${id}/image`)
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((blob) => {
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => {});
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, hasImage, baseUrl]);

  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return <div className="tcard-avatar">{src ? <img src={src} alt={name} /> : <div className="tcard-avatar-initials">{initials}</div>}</div>;
}

/* ─── Single testimonial card ──────────────────────────────────── */
function TestimonialCard({ item, delay }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        animation: visible ? `tFadeUp 0.55s ${delay}s ease both` : "none",
      }}
    >
      <div className="tcard">
        {/* Quote icon */}
        <div className="tcard-quote">
          <i className="flaticon-quote" />
        </div>

        {/* Stars */}
        <div className="tcard-stars">
          {[1, 2, 3, 4, 5].map((s) => (
            <i key={s} className="fa-solid fa-star" />
          ))}
        </div>

        {/* Message */}
        <p className="tcard-message">"{item.message}"</p>

        <div className="tcard-divider" />

        {/* Author row */}
        <div className="tcard-author">
          <Avatar id={item.id} name={item.name} hasImage={item.hasImage} />
          <div>
            <p className="tcard-name">{item.name}</p>
            <p className="tcard-role">Verified Patient</p>
          </div>
          <span className="tcard-verified">
            <i className="fa-solid fa-circle-check" />
            Verified
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Skeleton card ────────────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div className="tcard">
      <div style={{ display: "flex", gap: 4, marginBottom: 18 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="t-shimmer" style={{ width: 14, height: 14, borderRadius: "50%" }} />
        ))}
      </div>
      <div className="t-shimmer" style={{ height: 14, width: "100%", marginBottom: 10 }} />
      <div className="t-shimmer" style={{ height: 14, width: "92%", marginBottom: 10 }} />
      <div className="t-shimmer" style={{ height: 14, width: "78%", marginBottom: 10 }} />
      <div className="t-shimmer" style={{ height: 14, width: "60%", marginBottom: 28 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: "auto" }}>
        <div className="t-shimmer" style={{ width: 52, height: 52, borderRadius: "50%", flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div className="t-shimmer" style={{ height: 14, width: "55%", marginBottom: 8 }} />
          <div className="t-shimmer" style={{ height: 11, width: "35%" }} />
        </div>
      </div>
    </div>
  );
}

/* ─── Pagination ───────────────────────────────────────────────── */
function Pagination({ current, total, onChange }) {
  if (total <= 1) return null;
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 56 }}>
      {Array.from({ length: total }, (_, i) => i + 1).map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            border: p === current ? "none" : "1.5px solid #d1dce9",
            background: p === current ? "linear-gradient(135deg, #1b74bb, #0d3b6e)" : "#fff",
            color: p === current ? "#fff" : "#64748b",
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: p === current ? "0 4px 14px rgba(27,116,187,0.35)" : "none",
          }}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

/* ═══ Main page component ══════════════════════════════════════════ */
const TestimonialMain = ({ asSection = false }) => {
  const Tag = asSection ? "div" : "main";
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ totalPages: 1, totalItems: 0 });
  const ITEMS_PER_PAGE = 15;

  useEffect(() => {
    setLoading(true);
    makeRequest({ route: "/website/testimonial/public", method: "get" })
      .then((res) => {
        const all = res?.data || [];
        setMeta({
          totalItems: all.length,
          totalPages: Math.ceil(all.length / ITEMS_PER_PAGE),
        });
        const start = (page - 1) * ITEMS_PER_PAGE;
        setTestimonials(all.slice(start, start + ITEMS_PER_PAGE));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  const totalPages = meta.totalPages;
  const totalItems = meta.totalItems;

  return (
    <Tag>
      {!asSection && <Breadcrumb title="Patient Testimonials" subTitle="Testimonials" />}

      {/* ── Testimonial grid ──────────────────────────────────── */}
      <div style={{ background: "#f4f7fb", padding: "72px 0 80px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 28px" }}>
          {/* Section label */}
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "#edf5fb",
                border: "1.5px solid #bee0ef",
                borderRadius: 50,
                padding: "6px 20px",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: "#1b74bb",
              }}
            >
              <i className="fa-solid fa-quote-left" />
              Testimonials
            </span>
          </div>

          {loading ? (
            <div className="testimonial-grid">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : testimonials.length === 0 ? (
            <div style={{ textAlign: "center", padding: "80px 24px", color: "#94a3b8" }}>
              <div style={{ fontSize: 56, marginBottom: 16, animation: "tFloat 3s ease-in-out infinite" }}>💬</div>
              <p style={{ fontSize: 18, fontWeight: 600, color: "#64748b" }}>No testimonials yet.</p>
              <p style={{ fontSize: 14 }}>Check back soon to read what our patients have to say.</p>
            </div>
          ) : (
            <>
              <div className="testimonial-grid">
                {testimonials.map((item, idx) => (
                  <TestimonialCard key={item.id} item={item} delay={idx * 0.07} />
                ))}
              </div>
              <Pagination
                current={page}
                total={totalPages}
                onChange={(p) => {
                  setPage(p);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </>
          )}
        </div>
      </div>

      {/* ── CTA ───────────────────────────────────────────────── */}
      {!loading && (
        <div
          style={{
            background: "linear-gradient(135deg, #0a1628, #1b74bb)",
            padding: "60px 40px",
            textAlign: "center",
          }}
        >
          <h3
            style={{
              color: "#fff",
              fontSize: "clamp(20px, 2.5vw, 28px)",
              fontWeight: 800,
              margin: "0 0 10px",
            }}
          >
            Share Your Experience
          </h3>
          <p style={{ color: "rgba(255,255,255,0.68)", fontSize: 15, margin: "0 0 28px" }}>
            Your feedback helps us improve and inspires others to trust in our care.
          </p>
          <a
            href="/appointment"
            style={{
              display: "inline-block",
              background: "#fff",
              color: "#1b74bb",
              fontWeight: 700,
              fontSize: 14,
              padding: "13px 36px",
              borderRadius: 50,
              textDecoration: "none",
              boxShadow: "0 6px 22px rgba(0,0,0,0.2)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
              e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.28)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 6px 22px rgba(0,0,0,0.2)";
            }}
          >
            Book an Appointment
          </a>
        </div>
      )}
    </Tag>
  );
};

export default TestimonialMain;
